<script setup lang="ts">
import { ref, computed } from 'vue'
import { periodKeyToDateRange, periodKeyToTaxYearTitle } from '@/helpers/periodDisplay'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import { useCombinedStatementTotals } from '@/composables/useCombinedStatementTotals'
import StatementYearPanel from '@/components/dashboard/statements/StatementYearPanel.vue'
import type { DonorFinancialYear } from '@/types/donorFinancialYear'
import type { HistoricStatementYear } from '@/types/statementYears'

const props = defineProps<{
  year: HistoricStatementYear
  financialYear?: DonorFinancialYear
  isPaid: boolean
}>()

const expanded = ref(false)

const panelYear = computed((): DonorFinancialYear => {
  if (props.financialYear) return props.financialYear
  return {
    period: props.year.period,
    donations: [],
    totalDonations: props.year.totalDonated,
    totalGiftAid: props.year.totalGiftAid,
  }
})

// Combine the Swiftaid statement totals with the donor's manual donations so the
// collapsed header matches the combined hero shown in the expanded panel.
const { combinedDonatedPence, combinedGiftAidPence } = useCombinedStatementTotals(
  () => props.year.period,
  panelYear
)
</script>

<template>
  <div class="sa-hist-item" :class="{ 'sa-hist-item--expanded': isPaid && expanded }">
    <button
      type="button"
      class="sa-hist-head"
      :aria-expanded="isPaid ? expanded : false"
      :aria-controls="`hist-${year.period}`"
      :disabled="!isPaid"
      @click="isPaid && (expanded = !expanded)"
    >
      <div>
        <div class="sa-stmt-year">{{ periodKeyToTaxYearTitle(year.period) }}</div>
        <div class="sa-stmt-range">{{ periodKeyToDateRange(year.period) }}</div>
      </div>
      <div class="sa-hist-head__right">
        <div class="sa-stmt-amount">
          <div class="sa-stmt-donated">{{ formatMoneyFromPence(combinedDonatedPence) }}</div>
          <div class="sa-stmt-ga">
            {{ formatMoneyFromPence(combinedGiftAidPence) }} Gift Aid
          </div>
        </div>
        <svg
          v-if="isPaid"
          class="sa-hist-chevron"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clip-rule="evenodd"
          />
        </svg>
        <svg
          v-else
          class="sa-hist-lock"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
    </button>
    <div
      v-if="isPaid && expanded"
      :id="`hist-${year.period}`"
      class="sa-hist-detail"
    >
      <StatementYearPanel
        :financial-year="panelYear"
        :issued-statement="year.issuedStatement"
        embedded
        show-tag
      />
    </div>
  </div>
</template>
